import { CKButton } from 'clearkit';
import { FC, useState } from 'react';
import { Link } from 'react-router-dom';

import { useProfilePermissions } from '~/components/profile/useProfilePermissions';
import { ButtonContainer } from '~/components/unified/ButtonContainer';
import { SettingsCard } from '~/components/unified/SettingsCard';
import { BillingInfo } from '~/generated/graphql';
import { usePublishableKeys } from '~/pages/Batches/hooks/usePublishableKeys';

import { CardDetails, StripeForm } from './StripeForm';
import { dollarFormat } from './utils';

type FormPaymentInformationProps = {
  onChange: (value: BillingInfo) => void;
  billingInfo: BillingInfo;
  handleSave(value: BillingInfo): void;
  refetch: () => void;
};

export const FormPaymentInformation: FC<FormPaymentInformationProps> = ({
  billingInfo,
  refetch,
}) => {
  const [accountData, setAccountData] = useState<BillingInfo>(billingInfo);
  const [isEditing, setIsEditing] = useState(false);
  const { publishableKeys } = usePublishableKeys();
  const { isOwner } = useProfilePermissions();

  const handleToggleEdit = () => {
    setIsEditing((prevState) => !prevState);
  };
  const handleSave = async (value: CardDetails) => {
    await refetch();
    setAccountData((prevState) => ({
      ...prevState,
      cardLastFour: value.cardLastFour,
      cardType: value.cardType,
    }));
    setIsEditing(false);
  };
  return (
    <SettingsCard>
      <SettingsCard.Header>
        <SettingsCard.Heading>Payment Details</SettingsCard.Heading>
      </SettingsCard.Header>
      <SettingsCard.Body className="grid gap-6">
        <div className="transition-[height] duration-300 ease-out grid gap-6">
          <div>
            {accountData?.cardLastFour ? (
              <CardDetailsInfo billingInfo={accountData} />
            ) : (
              <div>No card on file</div>
            )}
          </div>
          {isEditing && publishableKeys.STRIPE_PUBLISHABLE_KEY ? (
            <StripeForm
              handleSave={handleSave}
              handleToggleEdit={handleToggleEdit}
              stripePublishableKey={publishableKeys.STRIPE_PUBLISHABLE_KEY}
            />
          ) : null}
        </div>
      </SettingsCard.Body>
      {isEditing ? null : (
        <SettingsCard.Footer className="flex justify-end">
          <ButtonContainer>
            <CKButton
              isDisabled={!isOwner}
              onClick={handleToggleEdit}
              variant="bold"
              variantColor="blue"
            >
              {accountData?.cardLastFour
                ? 'Update card on file'
                : 'Add new card'}
            </CKButton>
          </ButtonContainer>
        </SettingsCard.Footer>
      )}
    </SettingsCard>
  );
};

function CardDetailsInfo({ billingInfo }: { billingInfo: BillingInfo }) {
  const updateDate = new Date(billingInfo?.nextInvoiceAt);

  const formattedUpdateDate = updateDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <>
      <p>
        Your current card is a{' '}
        <span className="capitalize">{billingInfo?.cardType}</span> ending in{' '}
        <strong>{billingInfo?.cardLastFour}</strong>
      </p>
      {billingInfo?.showAutomaticBillingMessage ? (
        <p className="mt-4">
          Your credit card will automatically be charged{' '}
          <strong>
            {/* apihub stores amounts in cents */}
            {dollarFormat.format(
              (billingInfo.nextStripeInvoiceAmount as number) / 100,
            )}
          </strong>{' '}
          on {formattedUpdateDate} for another{' '}
          {billingInfo.billingTerm == 'annual' ? 'year' : 'month'} of service.
          You can view and update your{' '}
          <Link className="ck-link" to="/settings/billing/subscriptions">
            subscriptions here
          </Link>
          .
        </p>
      ) : null}
    </>
  );
}
